import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';

// Shared visual styles
const itemBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9em;
  transition: all 0.18s ease;
  text-decoration: none;
  border: none;
  cursor: pointer;
`;

const PageLink = styled(Link)`
  ${itemBase}
  background: ${props => props['aria-current'] === 'page'
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props['aria-current'] === 'page' ? '#667eea' : 'white'};
  outline: 2px solid ${props => props['aria-current'] === 'page'
    ? 'rgba(255,255,255,0.5)'
    : 'rgba(255,255,255,0.2)'};
  outline-offset: -2px;

  &:hover {
    background: ${props => props['aria-current'] === 'page'
      ? 'rgba(255,255,255,1)'
      : 'rgba(255,255,255,0.2)'};
    outline-color: rgba(255,255,255,0.5);
    transform: translateY(-1px);
  }
`;

const DisabledSpan = styled.span`
  ${itemBase}
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.25);
  outline: 2px solid rgba(255, 255, 255, 0.1);
  outline-offset: -2px;
  cursor: not-allowed;
  pointer-events: none;
`;

const Ellipsis = styled.span`
  ${itemBase}
  background: transparent;
  color: rgba(255, 255, 255, 0.35);
  cursor: default;
  min-width: 28px;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 40px;
  padding: 20px;
  flex-wrap: wrap;
`;

// SVG chevrons
const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const location = useLocation();

  if (totalPages <= 1) return null;

  const buildUrl = (page) => {
    const params = new URLSearchParams(location.search);
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ''}`;
  };

  const handleClick = (page) => {
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPages = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    if (endPage - startPage < showPages - 1) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <PageLink key={1} to={buildUrl(1)} onClick={() => handleClick(1)} aria-label="Page 1"
          aria-current={currentPage === 1 ? 'page' : undefined}>1</PageLink>
      );
      if (startPage > 2) pages.push(<Ellipsis key="e1" aria-hidden="true">…</Ellipsis>);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageLink key={i} to={buildUrl(i)} onClick={() => handleClick(i)}
          aria-label={`Page ${i}`} aria-current={currentPage === i ? 'page' : undefined}>
          {i}
        </PageLink>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push(<Ellipsis key="e2" aria-hidden="true">…</Ellipsis>);
      pages.push(
        <PageLink key={totalPages} to={buildUrl(totalPages)} onClick={() => handleClick(totalPages)}
          aria-label={`Page ${totalPages}`} aria-current={currentPage === totalPages ? 'page' : undefined}>
          {totalPages}
        </PageLink>
      );
    }

    return pages;
  };

  return (
    <Nav aria-label="Petition list pages">
      {currentPage > 1 ? (
        <PageLink to={buildUrl(currentPage - 1)} onClick={() => handleClick(currentPage - 1)} aria-label="Previous page">
          <ChevronLeft />
        </PageLink>
      ) : (
        <DisabledSpan aria-label="Previous page" aria-disabled="true"><ChevronLeft /></DisabledSpan>
      )}

      {renderPages()}

      {currentPage < totalPages ? (
        <PageLink to={buildUrl(currentPage + 1)} onClick={() => handleClick(currentPage + 1)} aria-label="Next page">
          <ChevronRight />
        </PageLink>
      ) : (
        <DisabledSpan aria-label="Next page" aria-disabled="true"><ChevronRight /></DisabledSpan>
      )}
    </Nav>
  );
};

export default Pagination;
